import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const DocumentUploadModal = ({ isOpen, onClose, onUpload, transactions }) => {
  const [dragActive, setDragActive] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadData, setUploadData] = useState({
    transactionId: '',
    documentType: '',
    description: ''
  });
  const fileInputRef = useRef(null);

  const documentTypeOptions = [
    { value: '', label: 'Select document type...' },
    { value: 'deed', label: 'Property Deed' },
    { value: 'contract', label: 'Purchase Contract' },
    { value: 'assessment', label: 'Property Assessment' },
    { value: 'certificate', label: 'Certificate' },
    { value: 'report', label: 'Inspection Report' },
    { value: 'other', label: 'Other Documents' }
  ];

  const transactionOptions = [
    { value: '', label: 'Select transaction...' },
    ...transactions?.map(transaction => ({
      value: transaction?.id,
      label: `#${transaction?.id} - ${transaction?.propertyAddress}`
    }))
  ];

  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === 'dragenter' || e?.type === 'dragover') {
      setDragActive(true);
    } else if (e?.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);
    
    if (e?.dataTransfer?.files && e?.dataTransfer?.files?.[0]) {
      handleFiles(e?.dataTransfer?.files);
    }
  };

  const handleFiles = (files) => {
    const fileArray = Array.from(files);
    const validFiles = fileArray?.filter(file => {
      const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      return validTypes?.includes(file?.type) && file?.size <= 10 * 1024 * 1024; // 10MB limit
    });
    
    setSelectedFiles(prev => [...prev, ...validFiles]);
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev?.filter((_, i) => i !== index));
  };

  const handleUpload = () => {
    if (selectedFiles?.length > 0 && uploadData?.transactionId && uploadData?.documentType) {
      onUpload({
        files: selectedFiles,
        ...uploadData
      });
      
      // Reset form
      setSelectedFiles([]);
      setUploadData({
        transactionId: '',
        documentType: '',
        description: ''
      });
      onClose();
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  const getFileIcon = (file) => {
    if (file?.type?.includes('pdf')) return 'FileText';
    if (file?.type?.includes('image')) return 'Image';
    if (file?.type?.includes('word')) return 'FileSignature';
    return 'File';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg border border-border shadow-elevated w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Upload Documents</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Add documents to your transaction with automatic categorization
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Transaction and Type Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Transaction"
              options={transactionOptions}
              value={uploadData?.transactionId}
              onChange={(value) => setUploadData(prev => ({ ...prev, transactionId: value }))}
              required
              searchable
            />
            
            <Select
              label="Document Type"
              options={documentTypeOptions}
              value={uploadData?.documentType}
              onChange={(value) => setUploadData(prev => ({ ...prev, documentType: value }))}
              required
            />
          </div>

          {/* Description */}
          <Input
            label="Description (Optional)"
            type="text"
            placeholder="Brief description of the documents..."
            value={uploadData?.description}
            onChange={(e) => setUploadData(prev => ({ ...prev, description: e?.target?.value }))}
          />

          {/* File Upload Area */}
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-smooth ${
              dragActive 
                ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 hover:bg-muted/30'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <div className="space-y-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <Icon name="Upload" size={32} className="text-primary" />
              </div>
              
              <div>
                <p className="text-lg font-medium text-foreground">
                  Drop files here or click to browse
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Supports PDF, DOC, DOCX, JPG, PNG up to 10MB each
                </p>
              </div>
              
              <Button
                variant="outline"
                onClick={() => fileInputRef?.current?.click()}
                iconName="FolderOpen"
                iconPosition="left"
              >
                Choose Files
              </Button>
              
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={(e) => handleFiles(e?.target?.files)}
                className="hidden"
              />
            </div>
          </div>

          {/* Selected Files */}
          {selectedFiles?.length > 0 && (
            <div className="space-y-3">
              <h3 className="font-medium text-foreground">Selected Files ({selectedFiles?.length})</h3>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {selectedFiles?.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Icon name={getFileIcon(file)} size={16} className="text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-foreground text-sm">{file?.name}</p>
                        <p className="text-xs text-muted-foreground">{formatFileSize(file?.size)}</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeFile(index)}
                    >
                      <Icon name="X" size={16} />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border">
          <div className="text-sm text-muted-foreground">
            {selectedFiles?.length > 0 && (
              <span>{selectedFiles?.length} file{selectedFiles?.length !== 1 ? 's' : ''} ready to upload</span>
            )}
          </div>
          
          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleUpload}
              disabled={selectedFiles?.length === 0 || !uploadData?.transactionId || !uploadData?.documentType}
              iconName="Upload"
              iconPosition="left"
            >
              Upload Documents
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentUploadModal;